import { useState, useEffect, useCallback, useRef } from 'react';
import Services from '../services/Services';
import { EventListItem } from '../types';
import { useGeneral } from '../contexts';

interface UseSavedEventsOptions {
  token: string | null;
  userID?: string;
  autoFetch?: boolean;
}

export const useSavedEvents = (options: UseSavedEventsOptions) => {
  const { token, userID, autoFetch = false } = options;
  const { toastSuccess, toastError, language } = useGeneral();
  const { getTranslation } = language;
  const [savedEvents, setSavedEvents] = useState<EventListItem[]>([]);
  const [savedEventIds, setSavedEventIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch just saved event IDs (lightweight, for bookmark state)
  const fetchSavedEventIds = useCallback(async () => {
    if (!userID || !token) {
      setSavedEventIds(new Set());
      return;
    }

    try {
      const response = await Services.GetSavedChannelEvents(token);
      const savedEventsList = response?.data?.result || [];

      if (Array.isArray(savedEventsList) && savedEventsList.length > 0) {
        const savedIds = new Set(
          savedEventsList
            .map((savedEvent) => savedEvent.eventID || savedEvent.id || '')
            .filter(Boolean)
        );
        setSavedEventIds(savedIds);
      } else {
        setSavedEventIds(new Set());
      }
    } catch (err) {
      setSavedEventIds(new Set());
    }
  }, [userID, token]);

  // Fetch saved events with full details
  const fetchSavedEvents = useCallback(async () => {
    if (!userID || !token) {
      setSavedEvents([]);
      setSavedEventIds(new Set());
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await Services.GetSavedChannelEvents(token);
      const savedEventsList = response?.data?.result || [];

      if (Array.isArray(savedEventsList) && savedEventsList.length > 0) {
        // First, update the IDs set
        const savedIds = new Set(
          savedEventsList
            .map((savedEvent) => savedEvent.eventID || savedEvent.id || '')
            .filter(Boolean)
        );
        setSavedEventIds(savedIds);

        // Then fetch full event details for each saved event
        const eventsWithDetails = await Promise.all(
          savedEventsList.map(async (savedEvent) => {
            const eventID = savedEvent.eventID || savedEvent.id;
            if (eventID) {
              try {
                const detailsResponse = await Services.SocialHiveGetEventDetails(token, eventID);
                if (detailsResponse?.data?.status && detailsResponse.data.result) {
                  return {
                    ...detailsResponse.data.result,
                    eventID: eventID,
                  } as EventListItem;
                }
              } catch (err) {
                // Error fetching event details
              }
            }
            return null;
          })
        );

        const validEvents = eventsWithDetails.filter((e): e is EventListItem => e !== null);
        setSavedEvents(validEvents);
      } else {
        setSavedEvents([]);
        setSavedEventIds(new Set());
      }
    } catch (err) {
      setSavedEvents([]);
      setSavedEventIds(new Set());
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [userID, token]);

  // Always fetch saved event IDs on mount when user is logged in (for bookmark state)
  useEffect(() => {
    if (userID && token) {
      fetchSavedEventIds();
    } else {
      setSavedEventIds(new Set());
    }
  }, [userID, token, fetchSavedEventIds]);

  // Auto-fetch full details on mount if enabled
  useEffect(() => {
    if (autoFetch && userID && token) {
      fetchSavedEvents();
    }
  }, [autoFetch, userID, token, fetchSavedEvents]);

  // Toggle bookmark (save/unsave)
  const toggleBookmark = useCallback(async (event: EventListItem) => {
    if (!token || !userID) {
      toastError(getTranslation('home.loginRequired'));
      return;
    }

    const eventID = event.eventID || '';
    const channelID = event.channelID || '';
    const isSaved = savedEventIds.has(eventID);

    if (!channelID || !eventID) {
      toastError(getTranslation('home.eventSaveError'));
      return;
    }

    try {
      if (isSaved) {
        const response = await Services.RemoveSavedChannelEvent(token, channelID, eventID);
        if (response?.data?.status) {
          setSavedEventIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(eventID);
            return newSet;
          });
          // Remove from saved events list
          setSavedEvents((prev) => prev.filter((e) => e.eventID !== eventID));
          toastSuccess(getTranslation('home.eventRemoved'));
        }
      } else {
        const response = await Services.SaveChannelEvent(token, channelID, eventID);
        if (response?.data?.status) {
          setSavedEventIds((prev) => new Set(Array.from(prev).concat([eventID])));
          toastSuccess(getTranslation('home.eventSaved'));
          // Optionally refresh saved events list
          if (autoFetch) {
            await fetchSavedEvents();
          }
        }
      }
      // Always refresh saved event IDs after toggle to ensure consistency
      await fetchSavedEventIds();
    } catch (error: any) {
      toastError(error.response?.data?.message || getTranslation('home.eventSaveError'));
    }
  }, [token, userID, savedEventIds, fetchSavedEvents, fetchSavedEventIds, autoFetch, toastSuccess, toastError, getTranslation]);

  // Check if event is saved
  const isEventSaved = useCallback((eventID: string) => {
    return savedEventIds.has(eventID);
  }, [savedEventIds]);

  return {
    savedEvents,
    savedEventIds,
    loading,
    fetchSavedEvents,
    fetchSavedEventIds,
    toggleBookmark,
    isEventSaved,
  };
};

